import { TinyColor } from '@ctrl/tinycolor'
import { Check, Clear } from '@mui/icons-material'
import type {} from '@mui/lab/themeAugmentation'
import { createTheme as createMuiTheme, ThemeOptions } from '@mui/material'
import { themeMeta } from './meta'

const createTheme = (themeColor?: string) => {
  const base = createMuiTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1280,
        xl: 1440,
      },
    },
    typography: {
      fontFamily: 'poppins, sans-serif',
      h1: {
        ...themeMeta.typography.titleBold,
      },
      h2: {
        ...themeMeta.typography.textLargeBold,
      },
      h3: {
        ...themeMeta.typography.textMediumBold,
      },
      h4: {
        ...themeMeta.typography.textSmallBold,
      },
      h5: {
        ...themeMeta.typography.textXsmallBold,
      },
      h6: {
        ...themeMeta.typography.textXsmallBold,
      },
      body1: {
        ...themeMeta.typography.textSmall,
      },
      body2: {
        ...themeMeta.typography.textXsmall,
      },
    },
    palette: {
      primary: {
        main: themeMeta.colors.primary.default,
        dark: themeMeta.colors.primary.dark,
        light: themeMeta.colors.primary.light,
        contrastText: themeMeta.colors.monochrome.offWhite,
      },
      secondary: {
        main: themeMeta.colors.secondary.default,
        dark: themeMeta.colors.secondary.dark,
        light: themeMeta.colors.secondary.light,
        contrastText: themeMeta.colors.monochrome.offWhite,
      },
      success: {
        main: themeMeta.colors.success.default,
        light: themeMeta.colors.success.light,
      },
      warning: {
        main: themeMeta.colors.warning.default,
        light: themeMeta.colors.warning.light,
      },
      error: {
        main: themeMeta.colors.error.default,
        dark: themeMeta.colors.error.dark,
        light: themeMeta.colors.error.light,
      },
      divider: themeMeta.colors.monochrome.line,
      text: {
        primary: themeMeta.colors.monochrome.offBlack,
        secondary: themeMeta.colors.monochrome.body,
      },
      action: {
        disabled: undefined,
        disabledBackground: undefined,
      },
    },
    transitions: {
      duration: {
        shortest: 100,
        leavingScreen: 110,
        shorter: 115,
        enteringScreen: 125,
        short: 130,
        standard: 150,
        complex: 200,
      },
    },
  })

  const light = new TinyColor(themeColor).isLight()
  const dark = new TinyColor(themeColor).isDark()

  const configured = createMuiTheme(
    base,
    (themeColor
      ? {
          palette: {
            primary: {
              main: themeColor,
              dark: new TinyColor(themeColor).darken(20).toString(),
              light: new TinyColor(themeColor).lighten(20).toString(),
              ...(light && {
                contrastText: base.palette.text.primary,
              }),
            },
          },
        }
      : {}) satisfies ThemeOptions
  )

  const theme = createMuiTheme(configured, {
    components: {
      MuiAppBar: {
        defaultProps: {
          color: 'inherit',
          elevation: 0,
        },
        styleOverrides: {
          root: {
            boxShadow: `0 0 ${configured.spacing(2)} rgba(0, 0, 0, 0.08)`,
          },
        },
      },
      MuiToolbar: {
        defaultProps: {
          disableGutters: true,
        },
        styleOverrides: {
          root: {
            gap: configured.spacing(1.5),

            [configured.breakpoints.up('xs')]: {
              minHeight: 90,
            },
          },
        },
      },
      MuiContainer: {
        defaultProps: {
          maxWidth: 'xl',
        },
        styleOverrides: {
          root: {
            [configured.breakpoints.up('sm')]: {
              paddingInline: configured.spacing(8),
            },
          },
        },
      },
      MuiFormControl: {
        defaultProps: {
          margin: 'normal',
        },
        styleOverrides: {
          root: {
            display: 'flex',
          },
        },
        variants: [
          {
            props: {
              margin: 'normal',
            },
            style: {
              marginBottom: configured.spacing(2),
            },
          },
        ],
      },
      MuiTextField: {
        defaultProps: {
          variant: 'filled',
        },
      },
      MuiInputLabel: {
        defaultProps: {
          shrink: true,
        },
        styleOverrides: {
          shrink: {
            ...configured.typography.body1,
            position: 'initial',
            marginBlockEnd: configured.spacing(0.5),
            fontWeight: 600,
            color: configured.palette.text.primary,
            transform: 'none',

            '&.Mui-focused': {
              color: configured.palette.text.primary,
            },
          },
          asterisk: {
            display: 'none',
          },
        },
      },
      MuiFilledInput: {
        defaultProps: {
          disableUnderline: true,
        },
        styleOverrides: {
          root: {
            border: '2px solid transparent',
            borderRadius: configured.spacing(1),
            transitionProperty: 'background-color, border-color',

            '&, &:hover': {
              backgroundColor: themeMeta.colors.monochrome.input,
            },
            '&.Mui-focused': {
              borderColor: configured.palette.text.primary,
              backgroundColor: themeMeta.colors.monochrome.offWhite,
            },
          },
          input: {
            height: 'auto',
            paddingInline: configured.spacing(3),
            paddingBlock: configured.spacing(1.75),
          },
        },
      },
      MuiSelect: {
        defaultProps: {
          variant: 'filled',
        },
        styleOverrides: {
          root: {
            border: '2px solid transparent',
            borderRadius: configured.spacing(1),
            transitionProperty: 'background-color, border-color',

            '&, &:hover': {
              backgroundColor: themeMeta.colors.monochrome.input,
            },
            '&.Mui-focused': {
              borderColor: configured.palette.text.primary,
              backgroundColor: themeMeta.colors.monochrome.offWhite,
            },
          },
          select: {
            paddingRight: `${configured.spacing(5)} !important`,
          },
          icon: {
            color: 'inherit',
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          disableElevation: true,
          loadingPosition: 'end',
        },
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: 999,
            textTransform: 'revert',

            '&.Mui-focusVisible': {
              outline: `8px solid ${configured.palette.primary.light}`,
            },
            '&.Mui-disabled': {
              opacity: 0.5,
              pointerEvents: 'none',
            },
          },
          outlined: {
            borderWidth: 2,

            '&.Mui-focusVisible': {
              borderColor: 'transparent',
              outlineColor: configured.palette.divider,
            },
          },
          sizeSmall: {
            ...configured.typography.h5,
            maxBlockSize: configured.spacing(5),
            paddingInline: configured.spacing(2),
            paddingBlock: configured.spacing(1.125),
          },
          sizeMedium: {
            ...configured.typography.h4,
            maxBlockSize: configured.spacing(7),
            paddingInline: configured.spacing(3),
            paddingBlock: configured.spacing(2),
          },
          loadingIndicator: {
            variants: [
              {
                props: { size: 'small', fullWidth: false },
                style: {
                  right: configured.spacing(2),
                },
              },
              {
                props: { size: 'medium', fullWidth: false },
                style: {
                  right: configured.spacing(3),
                },
              },
            ],
          },
        },
      },
      MuiTabs: {
        defaultProps: {
          variant: 'scrollable',
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',

            '&, &.Mui-selected': {
              color: configured.palette.text.primary,
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: configured.palette.primary.main,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            textDecorationColor: 'inherit',
            cursor: 'pointer',
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            gap: configured.spacing(1),
            marginInline: 0,
          },
          label: {
            ...configured.typography.body2,
            verticalAlign: 'middle',

            '& .MuiLink-root': {
              fontWeight: 600,
            },
          },
          asterisk: {
            display: 'none',
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          checkedIcon: (
            <Check
              htmlColor={
                dark
                  ? configured.palette.primary.contrastText
                  : configured.palette.secondary.dark
              }
            />
          ),
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            padding: 6,
            backgroundColor: themeMeta.colors.monochrome.input,

            '& .MuiSvgIcon-root': {
              fontSize: '1em',
              visibility: 'hidden',
            },
            '&.Mui-checked': {
              backgroundColor: configured.palette.primary.main,

              '& .MuiSvgIcon-root': {
                visibility: 'revert',
              },
            },
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          fullWidth: true,
          slotProps: {
            paper: {
              elevation: 0,
            },
          },
        },
        styleOverrides: {
          paper: {
            borderRadius: configured.spacing(3),
          },
          paperFullScreen: {
            alignSelf: 'end',
            blockSize: `calc(100% - ${configured.spacing(11)})`,
            borderEndStartRadius: 0,
            borderEndEndRadius: 0,
          },
        },
      },
      MuiDialogContent: {
        defaultProps: {
          dividers: true,
        },
        styleOverrides: {
          root: {
            paddingInline: configured.spacing(4),
            paddingBlockStart: 0,
            paddingBlockEnd: configured.spacing(4),
          },
          dividers: {
            borderTop: 'none',
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            justifyContent: 'space-between',
            gap: configured.spacing(3),
            padding: `${configured.spacing(3)} ${configured.spacing(5)}`,

            '&:has(:only-child)': {
              justifyContent: 'end',
            },
            '& .MuiButton-root': {
              marginLeft: 0,
            },

            [configured.breakpoints.down('sm')]: {
              '& .MuiButton-root': {
                flex: 1,
              },
            },
          },
        },
      },
      MuiChip: {
        defaultProps: {
          deleteIcon: <Clear />,
        },
        styleOverrides: {
          root: {
            ...themeMeta.typography.textXsmall,
            color: configured.palette.text.secondary,
            backgroundColor: themeMeta.colors.monochrome.input,
            borderRadius: configured.spacing(1),
          },
          deleteIcon: {
            fontSize: '1.25em',
            color: 'currentColor',

            '&:hover': {
              color: configured.palette.text.primary,
            },
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          elevation: 0,
          disableAutoFocusItem: true,
        },
        styleOverrides: {
          paper: {
            marginBlockStart: configured.spacing(1),
            border: `2px solid ${configured.palette.divider}`,
            borderRadius: configured.spacing(1),
          },
        },
      },
      MuiStack: {
        defaultProps: {
          useFlexGap: true,
        },
      },
    },
  } satisfies ThemeOptions)

  return theme
}

export { createTheme }
